import React from 'react';
import { RefreshCw, Trash2, ExternalLink } from 'lucide-react';
import { Certificate } from '../../hooks/useDomains';
// import { Domain } from '../../hooks/useDomains';

interface DomainCardProps {
  domain: Certificate;
  onRefresh: (domain: string) => void;
  onRemove: (domain: string) => void;
}

// Removed all date parsing logic since the backend now returns dates in the correct format.
const DomainCard: React.FC<DomainCardProps> = ({ domain, onRefresh, onRemove }) => {
  const getStatusColor = () => {
    switch (domain.status) {
      case 'valid':
        return 'bg-accent-100 text-accent-800 border-accent-200';
      case 'warning':
        return 'bg-warning-100 text-warning-800 border-warning-200';
      case 'expired':
        return 'bg-danger-100 text-danger-800 border-danger-200';
      case 'error':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusText = () => {
    switch (domain.status) {
      case 'valid':
        return 'Valid';
      case 'warning':
        return domain.days_remaining <= 7 ? 'Expiring Soon' : 'Warning';
      case 'expired':
        return 'Expired';
      case 'error':
        return 'Error';
      default:
        return 'Unknown';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden transition hover:shadow-md">
      <div className="p-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900 truncate" title={domain.name}>
            {domain.name}
          </h3>
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor()}`}
          >
            {getStatusText()}
          </span>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-500">Expires On</p>
            <p className="text-sm font-medium text-gray-900">{domain.expiry_date}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Days Remaining</p>
            <p className={`text-sm font-medium ${domain.days_remaining <= 0 ? 'text-danger-600' :
                domain.days_remaining <= 7 ? 'text-warning-600' :
                  'text-gray-900'
              }`}>
              {domain.days_remaining <= 0 ? 'Expired' : `${domain.days_remaining} days`}
            </p>
          </div>
        </div>

        <div className="mt-4 text-xs text-gray-500">
          Last checked: {domain.last_checked}
        </div>
      </div>

      <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
        <div className="flex justify-between">
          <button
            onClick={() => onRefresh(domain.name)}
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <RefreshCw className="h-3.5 w-3.5 mr-1" />
            Refresh
          </button>

          <div className="flex space-x-2">
            <a
              href={`https://${domain.name}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <ExternalLink className="h-3.5 w-3.5 mr-1" />
              Visit
            </a>

            <button
              onClick={() => onRemove(domain.name)}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-red-50 hover:text-danger-700 hover:border-danger-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-danger-500"
            >
              <Trash2 className="h-3.5 w-3.5 mr-1" />
              Remove
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DomainCard;